import { useEffect } from "react";
import { ID_TOKEN, signout, signoutPortal } from "../util/OAuth";
import {
  IonPage,
  IonLoading,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLabel,
  isPlatform,
} from "@ionic/react";
import { abpLogout, getCustomLogOutUrl } from "../api/abpLogin";
import { getApiSession } from "../util/ApiOptionsHelper";
import { remove } from "../util/Preferences";
import {
  deleteBiometricCredential,
  BIOMETRIC_PORTAL_SERVER,
} from "../util/Biometric";
import { Preferences } from "@capacitor/preferences";
import { Browser } from "@capacitor/browser";
import { App } from "@capacitor/app";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";

const Logout: React.FC = () => {
  useEffect(() => {
    logout();
  }, []);

  async function logout() {
    const isRelogin = globalThis.relogin ||
      new URLSearchParams(window.location.search).get("relogin") === "true";
    const apiSession = await getApiSession();
    if (apiSession && apiSession.LicenseSessionId) {
      //abp logout
      await getCustomLogOutUrl();
      try {
        await abpLogout();
      } catch (error) {
        // abpLogout can throw on Android/iOS when the session has already expired
        // and the server returns a non-JSON response. Safe to ignore - the local
        // logout and redirect must still proceed regardless.
        console.error("abpLogout failed during logout:", error);
      }
    }
    //skip portal siggnout for session refresh
    if (!isRelogin) {
      await signoutPortal();
    }
    //b2c logout. Returns true if it opened the interactive B2C sign-out page
    //(a real browser SSO session existed), false if it skipped it (the session
    //came from a silent biometric restore, so there was nothing to sign out).
    const openedInteractiveSignout = await signout(isRelogin);
    // A normal logout keeps biometric sign-in working: the credential itself
    // lives in the Keychain/Keystore (untouched by Preferences.clear), and we
    // re-persist the opt-in flag, the identity provider and the plant/DB config
    // so the next launch can offer biometric and restore the session. This only
    // applies when the user has biometric enabled; the explicit "Sign out &
    // remove biometric sign-in" action clears the opt-in first, so nothing is
    // preserved here and it becomes a full sign-out.
    const keepBiometric = (await Preferences.get({ key: "biometricEnabled" })).value === "true";
    if (keepBiometric) {
      // Keep-biometric logout: clear ONLY the session tokens so the app is truly
      // logged out, but keep all cached config (plant/DB list, portal token,
      // identity, bootstrap, etc.) so biometric can restore the session without
      // the user re-entering anything. The biometric fast path skips the portal
      // sign-in and mobileBootstrap, so that cached config must survive.
      // (The explicit "Sign out & remove biometric" action clears the opt-in
      // first, so this branch is skipped and everything is wiped below.)
      for (const key of [
        "BearerToken",
        "BearerTokenParsed",
        "CurrentB2CScope",
        "BearerTokenPortal",
        "BearerTokenPortalParsed",
      ]) {
        await Preferences.remove({ key });
      }
      // Strip the ABP license session that logout just ended on the server, so
      // biometric re-login starts a fresh license session (otherwise the login
      // would send a dead x-tw-licsession-id header and fail).
      const apiSessionRaw = (await Preferences.get({ key: "ApiSession" })).value;
      if (apiSessionRaw) {
        try {
          const apiSession = JSON.parse(apiSessionRaw);
          apiSession.LicenseSessionId = "";
          apiSession.LicenseIssuedAt = 0;
          apiSession.LicenseExpiration = 0;
          await Preferences.set({ key: "ApiSession", value: JSON.stringify(apiSession) });
        } catch (err) {
          console.log("Logout: could not strip license session from ApiSession", err);
        }
      }
    } else {
      // Full sign-out (biometric disabled / explicitly removed): clear everything.
      // The EAM/Portal refresh tokens are cached in the secure store on every
      // sign-in (not just when biometric is enabled), so a full sign-out must
      // delete them explicitly - Preferences.clear() does not touch the
      // Keychain/Keystore, and we must not leave a refresh token behind for a
      // user who is fully signing out.
      await deleteBiometricCredential();
      await deleteBiometricCredential(BIOMETRIC_PORTAL_SERVER);
      await Preferences.clear();
    }
    // await remove(ID_TOKEN);
    //reset autorelogin flag
    globalThis.relogin = false;
    // TODO: Check if user is working offline or not before clearing cache
    if (isRelogin) {
      // Session refresh: no interactive sign-out page is shown, so return to the
      // identity page immediately.
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } else if (!openedInteractiveSignout) {
      // Biometric-only session: signout() skipped the B2C sign-out page because
      // there was no browser SSO session to end. Go straight to the Identity page
      // instead of stranding the user on an empty "select an account" / "you
      // signed out" page that they would have to close by hand.
      window.location.href = "/";
    } else {
      // Real sign-out: signout() opened the B2C / IdP logout page in the in-app
      // browser and intentionally left it open so a shared-device user can finish
      // signing out of SSO. The end page is ALWAYS UserIdentity ("/").
      let done = false;
      let finishedHandle: { remove: () => void } | undefined;
      let urlOpenHandle: { remove: () => void } | undefined;
      const finish = () => {
        if (done) return;
        done = true;
        finishedHandle?.remove();
        urlOpenHandle?.remove();
        window.location.href = "/";
      };
      // Android's Custom Tab auto-closes when B2C redirects to the
      // post_logout_redirect_uri, firing browserFinished. Wait for that (or the
      // user dismissing the page) and then return to Identity.
      finishedHandle = await Browser.addListener("browserFinished", finish);
      if (isPlatform("ios") && isPlatform("hybrid")) {
        // iOS SFSafariViewController does NOT auto-dismiss on a custom-scheme
        // redirect (com.aptean.eam://...), so after B2C completes sign-out it
        // leaves a blank page the user must close by hand. Catch that redirect
        // via appUrlOpen and close the browser ourselves, matching Android. This
        // fires only AFTER B2C finishes, so the shared-device SSO sign-out still
        // completes; if the chain instead ends on the IdP "you signed out" page
        // (no redirect), no event fires and the user dismisses it as before.
        urlOpenHandle = await App.addListener("appUrlOpen", async (event) => {
          const url = event.url || "";
          if (
            url.startsWith("com.aptean.eam://oauth/auth") ||
            url.startsWith("msauth.com.aptean.eam://auth")
          ) {
            try {
              await Browser.close();
            } catch (e) {
              console.log("Browser.close after logout redirect failed:", e);
            }
            finish();
          }
        });
      }
    }
  }

  return (
    <IonPage>
      <IonLoading
        isOpen={true}
        backdropDismiss={false}
        duration={5000}
      />
      <IonHeader>
        <IonToolbar>
          <IonTitle>EAM Mobile</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonLabel>
          {(globalThis.relogin || new URLSearchParams(window.location.search).get("relogin") === "true")
            ? "Refreshing user session..."
            : "User is being logged off..."}
        </IonLabel>
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(reactPlugin, Logout, "Logout");
